import {
  FormControl,
  FormHelperText,
  FormLabel,
  Textarea,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Dialog, useDisclosure } from '@revelio/ui';

import { CardListSelect } from '../../../../../shared/ui/card-list';
import { useReportFilters } from '../../../../reports/hooks/useReportFilters';
import { FilterModal } from '../../../filters/components/FilterModal';
import {
  type TreeData,
  useFilterContext,
} from '../../../filters/contexts/FilterProvider';
import { getFilterDefinition } from '../../../filters/engine/filterRegistry';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
  type ListFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import modalStyles from '../../Modal/themedModal.module.css';
import { MAX_REPORT_COMPETITORS } from '../../PageConfigProvider/constants';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import styles from './parameter-entities-editor.module.css';
import {
  parameterUsesGlobalFilterMap,
  REPORT_DATE_RANGE_PRESETS,
} from './parameters';
import { useAddParameterFilterGroup } from './useAddParameterFilterGroup';
import { useCompetitorsFromPrimaryCompany } from './useCompetitorsFromPrimaryCompany';
import { transformCompetitorsToSelections } from './utils/transformCompetitorsToSelections';

export function ManualOverrideModal({
  isOpen,
  onClose,
  fieldLabel,
  initialValue,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  fieldLabel: string;
  initialValue: string;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    if (isOpen) {
      setDraft(initialValue);
    }
  }, [isOpen, initialValue]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Set Custom Value"
      size="md"
      className={modalStyles.content}
      footer={
        <>
          <Button size="sm" variant="ghost" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            color="lightBlue"
            onClick={() => {
              onSave(draft);
              onClose();
            }}
          >
            Save Value
          </Button>
        </>
      }
    >
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium">
          Value for {fieldLabel}
        </FormLabel>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. 123, 456"
          size="sm"
          minH="80px"
          resize="vertical"
          autoFocus
        />
        <FormHelperText fontSize="xs" color="gray.500" mt={2}>
          Enter a comma-separated list of IDs.
        </FormHelperText>
      </FormControl>
    </Dialog>
  );
}

export const ParameterEntitiesEditor = ({
  parameters,
  onCanCreateReport,
  showResetCompetitorsButton = false,
}: {
  parameters: ReportParameterConfig[];
  onCanCreateReport?: (canCreate: boolean) => void;
  showResetCompetitorsButton?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeParamId, setActiveParamId] = useState<string | null>(null);

  const {
    activeFilterMap,
    filterState,
    dispatch: filterDispatch,
    getFilterData,
  } = useFilterContext();
  const { getReadableValue, getCurrentTreeSelections } = useReportFilters();

  // Fetch competitors for the primary company
  const { competitors, isLoading: isLoadingCompetitors } =
    useCompetitorsFromPrimaryCompany({ parameters });

  const topCompetitors = (competitors ?? [])?.slice(0, MAX_REPORT_COMPETITORS);

  useAddParameterFilterGroup({ parameters });

  const editingParameter =
    parameters.find((p) => p.parameterId === activeParamId) || null;

  const filterForModal: Filter | null = (() => {
    if (!editingParameter) return null;
    const filterMapToUse = parameterUsesGlobalFilterMap(editingParameter)
      ? activeFilterMap
      : filterState.groupMap.get(editingParameter.parameterId);
    if (!filterMapToUse) return null;

    const current =
      filterMapToUse.get(editingParameter.filterId) ||
      getFilterDefinition(editingParameter.filterId);
    return current
      ? { ...current, isPrimary: editingParameter.isPrimary }
      : null;
  })();

  const openEditor = (parameterId: string) => {
    setActiveParamId(parameterId);
    onOpen();
  };

  const closeEditor = () => {
    onClose();
  };

  const isParameterSelected = useCallback(
    (parameter: ReportParameterConfig): boolean => {
      const filterMapToUse = parameterUsesGlobalFilterMap(parameter)
        ? activeFilterMap
        : filterState.groupMap.get(parameter.parameterId);
      if (!filterMapToUse) return false;

      const filter = filterMapToUse.get(parameter.filterId);
      if (!filter) return false;

      if (filter.type === FilterType.DATE_RANGE) {
        const dateRange = filter as DateRangeFilter;
        return !!dateRange.min && !!dateRange.max;
      }

      if (filter.type === FilterType.TREE) {
        const tree = filter as TreeFilter;
        if (parameter.isPrimary) {
          return (tree.primarySelections || []).length > 0;
        }
        return (tree.selections || []).length > 0;
      }

      if (
        filter.type === FilterType.LIST ||
        filter.type === FilterType.STATIC_LIST
      ) {
        const list = filter as ListFilter;
        return (list.selections || []).length > 0;
      }

      if (filter.type === FilterType.VALUE) {
        const val = (filter as SingleValueFilter<unknown>).value;
        return val !== null && val !== undefined && String(val).length > 0;
      }

      return true;
    },
    [filterState, activeFilterMap]
  );

  const lastCanCreateRef = useRef<boolean | null>(null);

  // Get company tree data for looking up competitor IDs
  const companyTreeData = useMemo(() => {
    const data = getFilterData(FilterId.COMPANY);
    return data as TreeData[] | null;
  }, [getFilterData]);

  const existingCompetitorsFilter = (() => {
    // Find the first comparison group parameter with FilterId.COMPETITORS
    const competitorsParameter = parameters?.[1];
    if (!competitorsParameter) return null;

    const comparisonGroupFilterMap = filterState.groupMap.get(
      competitorsParameter.parameterId
    );
    if (!comparisonGroupFilterMap) return null;

    return comparisonGroupFilterMap.get(FilterId.COMPANY);
  })();

  const setCompetitors = useCallback(() => {
    if (!topCompetitors || isLoadingCompetitors) return;

    const competitorsParameter = parameters?.[1];
    if (!competitorsParameter) return;

    if (
      !existingCompetitorsFilter ||
      existingCompetitorsFilter.type !== FilterType.TREE
    ) {
      return;
    }

    // Transform competitors to TreeSelection format
    const competitorSelections = transformCompetitorsToSelections({
      competitors: topCompetitors,
      companyTreeData,
    });

    if (competitorSelections.length === 0) return;

    // Get the filter definition to ensure we have all required properties
    const competitorsFilterDefinition = getFilterDefinition(FilterId.COMPANY);

    if (!competitorsFilterDefinition) return;

    // Update the comparison group filter with competitor selections
    const updatedFilter: TreeFilter = {
      ...competitorsFilterDefinition,
      ...(existingCompetitorsFilter || {}),
      selections: competitorSelections,
    };

    filterDispatch({
      type: 'SET_FILTER',
      filter: updatedFilter,
      groupKeyOverride: competitorsParameter.parameterId,
    });
  }, [
    companyTreeData,
    existingCompetitorsFilter,
    filterDispatch,
    parameters,
    topCompetitors,
    isLoadingCompetitors,
  ]);

  useEffect(() => {
    // Only populate if the competitors list is empty
    if (
      existingCompetitorsFilter &&
      existingCompetitorsFilter.type === FilterType.TREE &&
      existingCompetitorsFilter.selections.length > 0
    ) {
      return;
    }

    setCompetitors();
  }, [setCompetitors, existingCompetitorsFilter]);

  // Notify parent whether all parameters have selections
  // Recompute whenever parameters or filter state changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!onCanCreateReport) return;
    const canCreate = (() => {
      if (parameters.length === 0) return false;

      const primaryParam = parameters.find((p) => p.isPrimary);
      if (primaryParam) return isParameterSelected(primaryParam);

      return parameters.every((p) => p.isOptional || isParameterSelected(p));
    })();

    if (lastCanCreateRef.current === canCreate) return;
    lastCanCreateRef.current = canCreate;

    onCanCreateReport(canCreate);
  }, [parameters, filterState, onCanCreateReport, isParameterSelected]);

  return (
    <div>
      <div className={styles.parameterGrid}>
        {parameters.map((parameter) => {
          return (
            <CardListSelect<string>
              key={parameter.parameterId}
              id={parameter.parameterId}
              label={`${parameter.name}${parameter.isOptional ? '' : '*'}`}
              detail={
                getReadableValue({
                  ...(!parameterUsesGlobalFilterMap(parameter) && {
                    parameterKey: parameter.parameterId,
                  }),
                  filterId: parameter.filterId,
                }) || 'None Selected'
              }
              variant="report"
              isChecked={isParameterSelected(parameter)}
              onChange={() => openEditor(parameter.parameterId)}
            />
          );
        })}
        {showResetCompetitorsButton &&
          parameters?.[1] &&
          parameters?.[1].filterId === FilterId.COMPANY && (
            <Button
              size="sm"
              variant="link"
              color="primary"
              fullWidth
              style={{ gridColumnStart: 2 }}
              onClick={setCompetitors}
              disabled={isLoadingCompetitors || !topCompetitors?.length}
            >
              Reset competitors
            </Button>
          )}
      </div>
      {editingParameter && (
        <FilterModal
          isOpen={isOpen}
          onClose={closeEditor}
          title={editingParameter.name}
          filter={filterForModal}
          selectedFilterId={editingParameter.filterId}
          isCentered
          showSelectedValues
          selectionLimit={editingParameter.selectionLimit ?? 1}
          allowMultiLevelSelect={editingParameter.allowMultiLevelSelect}
          currentTreeSelections={getCurrentTreeSelections({
            ...(!parameterUsesGlobalFilterMap(editingParameter) && {
              parameterKey: editingParameter.parameterId,
            }),
            filterId: editingParameter.filterId,
          })}
          {...(!parameterUsesGlobalFilterMap(editingParameter) && {
            groupKeyOverride: editingParameter.parameterId,
          })}
          {...(editingParameter.filterId === FilterId.COMPANY && {
            disabledLevels: [0, 1],
          })}
          {...(editingParameter.filterId === FilterId.DATE_RANGE && {
            allowedPresetValues: REPORT_DATE_RANGE_PRESETS,
          })}
        />
      )}
    </div>
  );
};
